import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useHippoText } from '../../../../../hooks/useHippoText';
import { getAccEmploymentsSelector } from '../../../../../recoil/selectors/getAccEmploymentsSelector';
import type { AttributeChangeProps } from '../../../types';
import { Link, TypeaheadField } from '@flos/react-ui';
import { type OptionProps } from '@flos/react-ui/build/core/forms/select/select-input';
import { isOpcDataFilledAtom } from '../../../../../recoil/atoms/isOpcDataFilledAtom';
import { callMeVariantOpcAtom } from '../../../../../recoil/atoms/callMeVariantOpcAtom';
import { useMemo } from 'react';
import { getCoverableInfoSelector } from '../../../../../recoil/selectors/getCoverableInfoSelector';
import { isOpcFetchingAtom } from '../../../../../recoil/atoms/isOpcFetchingAtom';
import { getOPCCoverableAttributeSelector } from '../../../../../recoil/selectors/getOPCCoverableAttributeSelector';

const PROFESSIONAL_SPORT_EMPLOYMENT_CODE = '4047';

const AdultAccEmploymentChange = ({
    updateCoverableAttribute,
    additionalProps
}: AttributeChangeProps): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [isOpcDataFilled, setIsOpcDataFilled] = useRecoilState(isOpcDataFilledAtom);
    const currentOPCAttribute = useRecoilValue(getOPCCoverableAttributeSelector)?.adultAccident
        ?.employmentCode;
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const accEmployments = useRecoilValue(getAccEmploymentsSelector);
    const setCallMeVariant = useSetRecoilState(callMeVariantOpcAtom);
    const { isAccHighRiskORImmediateCompSelected } = useRecoilValue(getCoverableInfoSelector);

    const accEmploymentsOptions: OptionProps[] = accEmployments
        .map((employment) => ({
            label: employment.name,
            value: employment.code ?? ''
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const hazardAboveFiveEmployments = useMemo(
        () =>
            accEmployments
                .filter((employment) => Number(employment.hazardClass) > 5)
                .map((employment) => employment.code),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <>
            <h6>{getHippoText('DSS-COVERABLE-DETAILS-STEP-EMPLOYMENT-SUBHEADER')}</h6>
            <TypeaheadField
                label={getHippoText('DSS-OPC-INPUT-LABEL')}
                onValidityChange={(isValid) => {
                    setIsOpcDataFilled(!!isValid);
                }}
                options={accEmploymentsOptions}
                required
                iconShape="search"
                onChange={(e) => {
                    const value = e.target.value;
                    if (hazardAboveFiveEmployments.includes(value)) {
                        setCallMeVariant('hazardAboveFiveEmployment');
                        return;
                    }
                    if (
                        value === PROFESSIONAL_SPORT_EMPLOYMENT_CODE &&
                        isAccHighRiskORImmediateCompSelected
                    ) {
                        setCallMeVariant('professionalSportEmployment');
                        return;
                    }
                    updateCoverableAttribute({ employmentCode: value });
                    // onChange won't trigger if users delete value
                    setIsOpcDataFilled(true);
                }}
                disabled={isOpcFetching}
                value={currentOPCAttribute}
            />
            {!isOpcDataFilled && (
                <div className="u-spacing-inset-bottom-6">
                    <EmploymentNotOnList />
                </div>
            )}
        </>
    );
};

const EmploymentNotOnList = (): React.JSX.Element => {
    const getHippoText = useHippoText();
    const setCallMeVariant = useSetRecoilState(callMeVariantOpcAtom);

    return (
        <Link
            onClick={() => {
                setCallMeVariant('employmentNotInList');
            }}
        >
            {getHippoText('DSS-COVERABLE-DETAILS-STEP-EMPLOYMENT-NOT-ON-LIST')}
        </Link>
    );
};

export default AdultAccEmploymentChange;
